/**
 * Resilience utilities to prevent app freezes and crashes during/after deployments.
 * Catches dynamic import failures (e.g. stale chunks returning 404 after deploy)
 * and safely triggers a page reload with loop prevention.
 */

export const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false
  const message = (err instanceof Error ? err.message : String(err)) || ''
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /loading chunk .* failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

export const canTriggerChunkReload = (cooldownMs = 10000): boolean => {
  try {
    if (typeof sessionStorage === 'undefined') return true
    const lastReload = sessionStorage.getItem('last_chunk_reload')
    const now = Date.now()
    if (!lastReload || now - parseInt(lastReload, 10) > cooldownMs) {
      sessionStorage.setItem('last_chunk_reload', now.toString())
      return true
    }
  } catch {
    return true
  }
  return false
}

export const handleChunkLoadFailure = (targetUrl?: string): boolean => {
  if (canTriggerChunkReload()) {
    if (typeof window !== 'undefined') {
      if (targetUrl && window.location.assign) {
        window.location.assign(targetUrl)
      } else if (window.location.reload) {
        window.location.reload()
      }
    }
    return true
  }
  return false
}

export const setupResilienceListeners = (): void => {
  if (typeof window === 'undefined') return

  // 1. Vite-specific chunk preload errors
  window.addEventListener('vite:preloadError' as any, (event: any) => {
    console.warn('[Resilience] Vite preload error detected, reloading to fetch latest build:', event?.payload?.message || event?.payload || event?.type || 'preloadError')
    handleChunkLoadFailure()
  })

  // 2. Unhandled promise rejections (often emitted by dynamic import() failures)
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      console.warn('[Resilience] Dynamic import failure in unhandled rejection:', event.reason?.message || event.reason)
      if (handleChunkLoadFailure()) {
        event.preventDefault()
      }
    }
  })
}
